import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { openDialog, useJiraContext } from 'src/services/jira';
import { useSession } from 'src/services/session';

import integration from 'src/lib/api/integration';
import jira from 'src/lib/api/jira';
import pagerduty from 'src/lib/api/pagerduty';

type RuleReference = {
  id: string,
  name: string,
  enabled: boolean,
  isV3Rule: boolean,
  config: {
    services: [{
      html_url: string,
      id: string,
      label: string,
    }],
    projects: [{
      html_url: string,
      key: string,
      label: string,
    }],
  }
};

type AllRulesInfo = {
  loading: ?boolean,
  error: ?any,
  rules: ?[RuleReference]
};

const Context = createContext(null);
let updateRuleInContext = () => {};
let forceReloadAll = () => {};

const defaultState : AllRulesInfo = {
  loading: true,
  error: null,
  rules: [],
};

export const deprecateKey = (rule, targetKey) => {
  let keys = Object.keys(rule);
  for (let key of keys) {
    if (rule[key] === targetKey) {
      rule.label = `${rule.label} (deprecated)`;
    }
    if (typeof rule[key] === 'object' && rule[key] !== null) {
      deprecateKey(rule[key], targetKey);
    }
  }
  return rule;
};

export const convertRuleV3ToRuleReference = (webhook) : RuleReference => {
  const {
    id,
    name,
    webhook_object: service = {},
    config: {
      jira: {
        project,
      } = {}
    } = {}
  } = webhook || {};

  const key = (project && project.key) || '';

  return ({
    id: `ruleV3-${id}`,
    name,
    enabled: true,
    isV3Rule: true,
    config: {
      services: [{
        html_url: service.html_url,
        label: service.name,
        id: service.id
      }],
      projects: [{
        html_url: undefined, //TODO need to find a base url and construct url like base/projects/key
        key,
        label: (project && project.name) || 'unknown project name'
      }],
    }
  });
};

const loadAllRUlesV4 = async () => {
  const { rules } = await integration.getAllRules();
  return rules || [];
};

const loadAllRulesV3 = async () => {
  const { webhooks } = await pagerduty.getWebhooks('', {
    include: [
      'blacklisted',
      'outbound_integrations',
      'webhook_objects',
    ],
    limit: 100,
    outbound_integration_keys: [
      'jira_server'
    ],
    total: true
  });
  return webhooks.map(w => convertRuleV3ToRuleReference(w));
};

const loadAllRules = (loadV3) => {
  const loaders = [loadAllRUlesV4()];
  if (loadV3) loaders.push(loadAllRulesV3());
  return Promise.all(loaders).then(data => data.reduce((acc, items) => acc.concat(items), []));
};

export const Provider = ({ children }) => {
  const { accountDomain } = useSession();
  const { settings } = useJiraContext();
  const fullDomain = (settings && settings.v3Domain && settings.v3Domain.value) || '';
  const jiraV3ConnectedToTheSameDomain = accountDomain && accountDomain === fullDomain.split('.')[0];

  const [rules, setRules] = useState(defaultState);
  const [consumers, setConsumers] = useState(0);
  const [forceReloadId, setForceReloadId] = useState(1);

  const addConsumer = useCallback(() => {
    setConsumers(x => x + 1);
    return () => setConsumers(x => x - 1);
  }, []);

  const hasConsumers = consumers > 0;

  useEffect(() => {
    let mounted = true;
    setRules(defaultState);
    if (hasConsumers) {
      loadAllRules(jiraV3ConnectedToTheSameDomain)
        .then(rules => mounted && setRules({ loading: false, rules, error: null }))
        .catch(error => mounted && setRules({ loading: false, rules: [], error }));
    }

    return () => { mounted = false; };
  }, [hasConsumers, forceReloadId, jiraV3ConnectedToTheSameDomain]);

  forceReloadAll = () => {
    setForceReloadId(x => x + 1);
  };

  updateRuleInContext = (newRule) => {
    const id = newRule.id;
    setRules({
      ...rules,
      rules: rules.rules.map(rule => (rule.id === id ? newRule : rule))
    });
  };

  const value = useMemo(() => ({ rules, addConsumer }), [addConsumer, rules]);
  return (
    <Context.Provider value={value}>
      {children}
    </Context.Provider>
  );
};

export const useAllRules = () : AllRulesInfo => {
  const { rules, addConsumer } = useContext(Context);
  useEffect(addConsumer, [addConsumer]);
  return rules;
};

export const updateRule = (id, body) => {
  return integration
    .updateRule(id, body)
    .then(r => { updateRuleInContext(r); return r; });
};

const openRuleDialog = (path, options = {}) => openDialog(`/integration-jira-ui/settings/modal/rule${path}`, {
  width: '80vw',
  minHeight: 1500,
  ...options,
  onClose: () => {
    forceReloadAll();
    if (options && options.onClose) options.onClose();
  }
});

export const openCreateRuleDialog = () => openRuleDialog(`/edit?isNew=true`);

export const openEditRuleDialog = (id) => {
  if (id.startsWith('ruleV3-')) {
    const [, webhookId] = id.split('ruleV3-');
    openRuleDialog(`/edit?id=${webhookId}&isLegacy=true&isNew=false`, { minHeight: 300 });
  } else {
    openRuleDialog(`/edit?id=${id}&isLegacy=false&isNew=false`);
  }
};

export const openRemoveRuleDialog = (id) => {
  if (id.startsWith('ruleV3-')) {
    const [, webhookId] = id.split('ruleV3-');
    openRuleDialog(`/remove-rule-v3?id=${webhookId}`, { width: '40vw', minHeight: 300 });
  } else {
    openRuleDialog(`/remove?id=${id}`, { width: '40vw', minHeight: 300 });
  }
};

export const setProjectLevelSidebarVisibilityWithRetry = async (projectKey, retries, count = 0) => {
  try {
    const currentValue = await jira.getProjectProperty(projectKey, 'pagerduty') || {};
    const newValue = { ...currentValue, hideSidebar: false };
    await jira.setProjectProperty(projectKey, 'pagerduty', newValue);
    return true;
  } catch (e) {
    if (count === retries) {
      console.error('Error setting project property `sidebarVisibleByDefault`: ', e);
      return false;
    }
    await wait((2 ** count) * 100);
    return setProjectLevelSidebarVisibilityWithRetry(projectKey, retries, count + 1);
  }
};

const wait = (ms) => new Promise((res) => setTimeout(res, ms));
